
import { supabase } from "@/integrations/supabase/client";

export class DataRetentionService {
  // Archive events older than specified months
  static async archiveOldEvents(monthsToKeep: number = 6) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - monthsToKeep);
      
      console.log(`[DataRetention] Archiving events older than ${cutoffDate.toISOString()}`);
      
      // Get count of events to archive
      const { data: eventsToArchive, error: countError } = await supabase
        .from('user_activity_events')
        .select('id')
        .lt('timestamp', cutoffDate.toISOString());

      if (countError) {
        console.error('[DataRetention] Error counting events to archive:', countError);
        return { success: false, error: countError };
      }

      const eventCount = eventsToArchive?.length || 0;
      
      if (eventCount === 0) {
        console.log('[DataRetention] No events to archive');
        return { success: true, archivedCount: 0 };
      }

      // For now, we'll delete old events. In production, you might want to 
      // move them to an archive table first
      const { error: deleteError } = await supabase
        .from('user_activity_events')
        .delete()
        .lt('timestamp', cutoffDate.toISOString());

      if (deleteError) {
        console.error('[DataRetention] Error deleting old events:', deleteError);
        return { success: false, error: deleteError };
      }

      console.log(`[DataRetention] Successfully archived ${eventCount} events`);
      return { success: true, archivedCount: eventCount };
    } catch (error) {
      console.error('[DataRetention] Unexpected error:', error);
      return { success: false, error };
    }
  }

  // Clean up old sessions
  static async cleanupOldSessions(monthsToKeep: number = 12) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setMonth(cutoffDate.getMonth() - monthsToKeep);
      
      const { data: sessionsToDelete, error: countError } = await supabase
        .from('user_sessions')
        .select('id')
        .lt('created_at', cutoffDate.toISOString());

      if (countError) {
        console.error('[DataRetention] Error counting sessions to delete:', countError);
        return { success: false, error: countError };
      }

      const sessionCount = sessionsToDelete?.length || 0;
      
      if (sessionCount === 0) {
        return { success: true, deletedCount: 0 };
      }

      const { error: deleteError } = await supabase
        .from('user_sessions')
        .delete()
        .lt('created_at', cutoffDate.toISOString());

      if (deleteError) {
        console.error('[DataRetention] Error deleting old sessions:', deleteError);
        return { success: false, error: deleteError };
      }

      console.log(`[DataRetention] Successfully deleted ${sessionCount} old sessions`);
      return { success: true, deletedCount: sessionCount };
    } catch (error) {
      console.error('[DataRetention] Unexpected error in session cleanup:', error);
      return { success: false, error };
    }
  }

  // Generate daily aggregations for cost-effective storage
  static async generateDailyAggregations(date?: Date) {
    try {
      const targetDate = date || new Date();
      const startOfDay = new Date(targetDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(targetDate);
      endOfDay.setHours(23, 59, 59, 999);

      console.log(`[DataRetention] Generating aggregations for ${startOfDay.toISOString()}`);

      // Aggregate events by user and event type for the day
      const { data: dayEvents, error } = await supabase
        .from('user_activity_events')
        .select('user_id, event_type, event_category')
        .gte('timestamp', startOfDay.toISOString())
        .lte('timestamp', endOfDay.toISOString());

      if (error) {
        console.error('[DataRetention] Error fetching day events:', error);
        return { success: false, error };
      }

      // Process aggregations
      const aggregations = new Map<string, any>();
      
      dayEvents?.forEach(event => {
        const key = `${event.user_id}_${event.event_type}_${event.event_category}`;
        if (aggregations.has(key)) {
          aggregations.get(key).count += 1;
        } else {
          aggregations.set(key, {
            user_id: event.user_id,
            event_type: event.event_type,
            event_category: event.event_category,
            count: 1,
            date: startOfDay.toISOString().split('T')[0]
          });
        }
      });

      console.log(`[DataRetention] Generated ${aggregations.size} aggregation records`);
      return { 
        success: true, 
        aggregationCount: aggregations.size,
        aggregations: Array.from(aggregations.values())
      };
    } catch (error) {
      console.error('[DataRetention] Unexpected error in aggregation:', error);
      return { success: false, error };
    }
  }
}
